import { styled } from "goober";
import { useMemo } from "react";
import {
  getAIProvider,
  MANAGED_AI_PROVIDER_ID,
  resolveAIProviderId,
} from "../../../shared/aiProviders";
import { isManagedAiConnectorId } from "../../../shared/aiProviders/managedAiConnector";
import { ConnectorLogoContainer } from "../ConnectorLogoContainer";
import { makeUrl, routes } from "../../lib/urls";
import { useUrlSort } from "../../hooks/useUrlSort";
import { useMonitoringInteraction } from "./analytics";
import { GhostPill } from "../GhostPill";
import { ListTable } from "../ListTable";
import { ProviderLogo } from "../ProviderLogo";
import { Text } from "../Text";
import {
  type AiUsageRecord,
  computeModelUsageRows,
  formatCacheHitRate,
  formatEstimatedCostUsd,
  formatTokenCount,
  type ModelUsageRow,
} from "./aiUsage";
import { NameBody, NameCell, ScrollArea, TableCard } from "./monitoringList";
import { Panel, PanelHeader } from "./workflowsLayout";

const GhostModelLogo = styled("div")`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--alpha8);
  flex-shrink: 0;
`;

const GHOST_MODEL_ROWS = [
  { model: 112, provider: 64, tokens: 40, cache: 32, cost: 44 },
  { model: 88, provider: 52, tokens: 36, cache: 28, cost: 40 },
  { model: 136, provider: 72, tokens: 44, cache: 36, cost: 48 },
  { model: 104, provider: 60, tokens: 38, cache: 30, cost: 42 },
  { model: 124, provider: 68, tokens: 42, cache: 34, cost: 46 },
];

function cacheRate(row: ModelUsageRow): number {
  const denominator = row.inputTokens + row.cacheReadTokens;
  return denominator === 0 ? 0 : row.cacheReadTokens / denominator;
}

type Props = {
  records: readonly AiUsageRecord[];
  prices: ReadonlyMap<string, number>;
  connectorLabels: ReadonlyMap<string, string>;
};

export function AiModelsTablePanel({
  records,
  prices,
  connectorLabels,
}: Props) {
  const recordInteraction = useMonitoringInteraction();
  const { sortColumn, sortDirection, handleSort, sortableColumns } = useUrlSort(
    routes.monitoring.ai,
    { tokens: "desc", cache: "desc", cost: "desc" }
  );

  const rows = useMemo(
    () => computeModelUsageRows(records, prices),
    [records, prices]
  );

  const sorted = useMemo(() => {
    const dir = sortDirection === "asc" ? 1 : -1;
    const value = (row: ModelUsageRow): number => {
      switch (sortColumn) {
        case "tokens":
          return row.totalTokens;
        case "cache":
          return cacheRate(row);
        default:
          return row.estimatedCostUsd ?? 0;
      }
    };
    return [...rows].sort(
      (a, b) => dir * (value(a) - value(b)) || a.model.localeCompare(b.model)
    );
  }, [rows, sortColumn, sortDirection]);

  const items =
    rows.length === 0
      ? GHOST_MODEL_ROWS.map((row, index) => ({
          key: `ghost-${index}`,
          model: (
            <NameCell aria-hidden>
              <GhostModelLogo />
              <NameBody>
                <GhostPill style={{ width: row.model }} />
                <GhostPill style={{ width: row.provider }} />
              </NameBody>
            </NameCell>
          ),
          tokens: <GhostPill aria-hidden style={{ width: row.tokens }} />,
          cache: <GhostPill aria-hidden style={{ width: row.cache }} />,
          cost: <GhostPill aria-hidden style={{ width: row.cost }} />,
        }))
      : sorted.map((row) => {
          const providerId = resolveAIProviderId({
            provider: row.provider,
          });
          // Managed usage has a synthetic connector id with no connector modal.
          const managed = isManagedAiConnectorId(row.connectorId ?? "");
          const label =
            connectorLabels.get(row.connectorId ?? "") ??
            getAIProvider(providerId).label;
          return {
            key: `${row.connectorId ?? ""}\0${row.model}`,
            link:
              !managed && row.connectorId
                ? makeUrl(routes.modal.connector, {
                    id: row.connectorId,
                    tab: "usage",
                  })
                : makeUrl(routes.modal.aiProvider, {
                    id: row.aiProviderId ?? MANAGED_AI_PROVIDER_ID,
                  }),
            model: (
              <NameCell>
                <ConnectorLogoContainer
                  size={32}
                  borderRadius={8}
                  outline={false}
                  hoverBackground={false}
                >
                  <ProviderLogo provider={providerId} size={16} />
                </ConnectorLogoContainer>
                <NameBody>
                  <Text variant="text-regular-md" ellipsis>
                    {row.model}
                  </Text>
                  <Text
                    variant="text-small"
                    color="var(--text-secondary)"
                    ellipsis
                  >
                    {label}
                  </Text>
                </NameBody>
              </NameCell>
            ),
            tokens: (
              <Text variant="text-small">
                {formatTokenCount(row.totalTokens)}
              </Text>
            ),
            cache: (
              <Text variant="text-small">
                {formatCacheHitRate(row.cacheReadTokens, row.inputTokens)}
              </Text>
            ),
            cost: (
              <Text variant="text-small">
                {formatEstimatedCostUsd(row.estimatedCostUsd)}
              </Text>
            ),
          };
        });

  return (
    <Panel style={{ minHeight: 0 }}>
      <PanelHeader>
        <Text variant="text-small-md">Models</Text>
        <Text variant="text-small" color="var(--text-secondary)">
          Tokens, cache hit rate, and estimated cost per model in the last 30
          days.
        </Text>
      </PanelHeader>
      <TableCard aria-hidden={rows.length === 0 || undefined}>
        <ScrollArea>
          <ListTable
            headers={{
              model: "Model",
              tokens: "Tokens",
              cache: "Cache",
              cost: "Est. cost",
            }}
            columns={{
              model: "flex",
              tokens: "md",
              cache: "md",
              cost: "md",
            }}
            sortableColumns={rows.length === 0 ? [] : sortableColumns}
            sortColumn={rows.length === 0 ? null : sortColumn}
            sortDirection={sortDirection}
            onSort={
              rows.length === 0
                ? undefined
                : (column) => {
                    recordInteraction("ai.model_table_sort", { value: column });
                    handleSort(column);
                  }
            }
            items={items}
          />
        </ScrollArea>
      </TableCard>
    </Panel>
  );
}
